import type { DiscoveryReelPreview, DiscoveryResultCard } from '@discovr/types';
import type { SHEET_METRICS } from '../constants/findOptions';
import { CONSISTENCY_MIN_REELS, SCORE_PART_MAX } from '../constants/findLimits';
import { MS_PER_DAY, PERCENT } from '../constants/units';

export type ReelMetric = (typeof SHEET_METRICS)[number]['key'];

type ReelCounts = Pick<DiscoveryReelPreview, 'views' | 'likes' | 'comments'>;
type ReelDated = ReelCounts & Pick<DiscoveryReelPreview, 'postedAt'>;

const interactionsOf = (reel: ReelCounts) =>
  reel.likes === null && reel.comments === null ? null : (reel.likes ?? 0) + (reel.comments ?? 0);

const known = (values: readonly (number | null)[]) =>
  values.filter((value): value is number => value !== null);

const mean = (values: readonly number[]) =>
  values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;

function median(values: readonly number[]): number | null {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length / 2;
  return mean(sorted.slice(Math.ceil(mid) - 1, Math.floor(mid) + 1));
}

export function reelValue(reel: ReelCounts, metric: ReelMetric, followers: number): number | null {
  if (metric === 'likes') return reel.likes ?? null;
  if (metric === 'views') return reel.views ?? null;
  const interactions = interactionsOf(reel);
  return interactions === null || !followers ? null : (interactions / followers) * PERCENT;
}

export interface ReelChart {
  values: readonly (number | null)[];
  top: number;
  avg: number;
  above: number;
  known: number;
}

export function reelChart(
  reels: readonly ReelCounts[],
  metric: ReelMetric,
  followers: number,
): ReelChart {
  const values = reels.map((reel) => reelValue(reel, metric, followers));
  const counted = known(values);
  const avg = mean(counted) ?? 0;
  return {
    values,
    top: Math.max(...counted, 1),
    avg,
    above: counted.filter((value) => value >= avg).length,
    known: counted.length,
  };
}

function gapDays(reels: readonly ReelDated[]): number | null {
  const times = known(reels.map((reel) => (reel.postedAt ? Date.parse(reel.postedAt) : null)))
    .filter((time) => !Number.isNaN(time))
    .sort((a, b) => a - b);
  const first = times[0];
  const last = times.at(-1);
  if (times.length < 2 || first === undefined || last === undefined) return null;
  return (last - first) / (times.length - 1) / MS_PER_DAY;
}

function viewConsistency(views: readonly number[]): number | null {
  const avg = mean(views);
  const typical = median(views);
  if (views.length < CONSISTENCY_MIN_REELS || !avg || typical === null) return null;
  return Math.min(SCORE_PART_MAX, (typical / avg) * SCORE_PART_MAX);
}

export interface CardStats {
  engagement: number | null;
  avgViews: number | null;
  reach: number | null;
  perPost: number | null;
  gapDays: number | null;
  consistency: number | null;
}

export type StatsCard = Pick<
  DiscoveryResultCard,
  'reels' | 'followers' | 'erPct' | 'avgViews' | 'reachRatio' | 'avgInteractions' | 'avgGapDays'
>;

function reelStats(reels: readonly ReelDated[], followers: number): CardStats {
  const views = known(reels.map((reel) => reel.views));
  const avgViews = mean(views);
  const perPost = mean(known(reels.map(interactionsOf)));
  return {
    engagement: perPost !== null && followers ? (perPost / followers) * PERCENT : null,
    avgViews,
    reach: avgViews !== null && followers ? avgViews / followers : null,
    perPost,
    gapDays: gapDays(reels),
    consistency: viewConsistency(views),
  };
}

const profileStats = (card: StatsCard): CardStats => ({
  engagement: card.erPct,
  avgViews: card.avgViews,
  reach: card.reachRatio,
  perPost: card.avgInteractions,
  gapDays: card.avgGapDays,
  consistency: null,
});

const counted = (reel: ReelCounts) => reel.views !== null || interactionsOf(reel) !== null;

export const cardStats = (card: StatsCard): CardStats =>
  card.reels.some(counted) ? reelStats(card.reels, card.followers) : profileStats(card);
